import React, { useState } from 'react';
import { Button, Card, CardBody, Col, Table } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiChevronLeft } from '@mdi/js';
import { useNavigate } from 'react-router-dom';
import downloadExcelIcon from '../../../../assets/icons/downloadexcelicon.png';
import printIcon from '../../../../assets/icons/printicon.png';
import { fetchAllCountryCurrency } from '../../../../store/apis/expenseService';
import { useExpenseClaimCtx } from '../../context/ExpenseClaimProvider';
import { useExpenseClaimUtilCtx } from '../../context/ExpenseClaimUtilProvider';
import {
  ModalWrapper,
  ModalTable,
  DisplayModal,
} from '../../../../components/Common';
import { CLAIM_FORM_TYPES } from '../EmployeeExpenseClaim/constants';
import {
  UploadAttachmentsModal,
  ViewAttachmentsModal,
  PrintModal,
  ExpenseClaimRow,
  AdvanceRecoveryModal,
} from '../../../../components/ExpenseClaims';
import { FileUtil, NumberUtil } from '../../../../utils';
import {
  firstHeaderColumn,
  secondHeaderColumn,
  thirdHeaderColumn,
} from './constants';

import './EmployeeExpenseClaimsDetail.scss';

function EmployeeExpenseClaims() {
  document.title = 'Expense Claims | Daikin Envision';
  const navigate = useNavigate();
  const {
    formMethods: methods,
    formFieldArray,
    expenseClaimHeaders,
    isModalOpen,
    setIsModalOpen,
    modalConfig,
    setModalConfig,
    viewAttachmentsModalOpen,
    viewAttachmentSrNo,
    setViewAttachmentsModalOpen,
    displayModalOpen,
    displayModalFile,
    setDisplayModalOpen,
  } = useExpenseClaimCtx();
  const {
    validateExpenseClaimMutation,
    printExpenseClaimMutation,
    printExpenseClaimAttachmentMutation,
    defaultCurrCode,
    getExpenseClaimDetailsMutation,
  } = useExpenseClaimUtilCtx();
  const [searchText, setSearchText] = useState('');
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [uploadAttachmentsModalOpen, setUploadAttachmentsModalOpen] =
    useState(false);
  const [advanceRecoveryModalOpen, setAdvanceRecoveryModalOpen] =
    useState(false);

  const { watch } = methods;
  const { fields, append, remove } = formFieldArray;
  const { data: currencyLists } = useQuery({
    queryKey: ['currencyLists', searchText],
    queryFn: () =>
      fetchAllCountryCurrency({
        prefixText: searchText,
        type: 'HR_CURRENCY',
        param1: '',
        param2: '',
      }),
  });

  const renderTableBody = () =>
    fields.map((item, index) => (
      <ExpenseClaimRow
        key={item.id}
        index={index}
        item={item}
        remove={remove}
        currencyLists={currencyLists}
        defaultCurrCode={defaultCurrCode}
        setSearchText={setSearchText}
        methods={methods}
        watch={watch}
        append={append}
        totalRows={fields.length}
        formType={CLAIM_FORM_TYPES.VIEW}
      />
    ));

  const handleAttachments = async () => {
    setUploadAttachmentsModalOpen(true);
  };

  const handlePrint = async () => {
    if (!expenseClaimHeaders?.claimNo) {
      toast.error('No claim number found to print.', {
        className: 'toast-error',
      });
      return;
    }
    const response = await validateExpenseClaimMutation.mutateAsync({
      claimNo: expenseClaimHeaders?.claimNo,
      claimType: 'P',
    });

    if (response?.flag === 'True') {
      await printExpenseClaimMutation.mutateAsync({
        claimNo: expenseClaimHeaders?.claimNo,
      });
      toast.success(
        `Claim (${expenseClaimHeaders?.claimNo}) is ready for printing.`,
        {
          className: 'toast-success',
        },
      );
      setIsPrintModalOpen(true);
      return;
    }
    if (response?.flag === 'False') {
      setModalConfig({
        title: `Expense Claim ${expenseClaimHeaders?.claimNo} printing failed due to error specified below:`,
        data: response?.errorData,
        dataConfig: null,
      });
      setIsModalOpen(true);
    }
  };

  const handlePrintExcel = async () => {
    if (!expenseClaimHeaders?.claimNo) {
      toast.error('No claim number found to download as Excel.', {
        className: 'toast-error',
      });
      return;
    }
    try {
      const printResponseBlob =
        await printExpenseClaimAttachmentMutation.mutateAsync({
          docType: 'DOC_TYPE_EXPENSE_CLAIM',
          claimNo: expenseClaimHeaders?.claimNo || '',
          type: 'EXCEL',
        });
      FileUtil.downloadBlobFile(
        printResponseBlob,
        `Expense_Claim_${expenseClaimHeaders?.claimNo}.xlsx`,
      );
    } catch (error) {
      toast.error(
        error?.message || 'Failed to download expense claim as Excel.',
        {
          className: 'toast-error',
        },
      );
    }
  };

  const handleTransaction = async () => {
    const transactionResponse =
      await getExpenseClaimDetailsMutation.mutateAsync({
        claimNo: expenseClaimHeaders?.claimNo || '',
        employeeId: expenseClaimHeaders?.employeeId || '',
        listType: 'JOURNAL',
      });
    setModalConfig({
      title: 'GL Details',
      data: transactionResponse || [],
      dataConfig: null,
      size: 'xl',
      index: true,
    });
    setIsModalOpen(true);
  };

  const checkHRRoute = () => {
    const currentPath = window.location.pathname;
    return currentPath.includes('/hr/');
  };

  const handleBack = () => {
    if (checkHRRoute()) {
      navigate('/hr/expense/enquiry');
    } else {
      navigate(-1);
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="page-content expense-claim-detail-page">
        <Card className="eec-card">
          <CardBody className="p-0">
            {/* Header */}
            <div className="eec-header">
              {/* Expense Claim Headers */}
              <div className="eec-header-title px-2 px-2 d-flex flex-row align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="p-0 btn btn-link trd-back d-flex align-items-center"
                    onClick={() => handleBack()}
                  >
                    <Icon
                      path={mdiChevronLeft}
                      className="icon-chevron-left"
                      size={1.1}
                    />
                    <span className="back fs-5">Back</span>
                  </button>
                  <div
                    style={{
                      borderLeft: '1px solid lightgrey',
                      height: '30px',
                    }}
                  />
                  <span className="fw-semibold fs-5">Expense Claim</span>
                </div>
              </div>
              {/* Claim Card Info */}
              <div className="d-flex gap-2 px-2 py-2 flex-row align-items-center justify-content-between">
                <Col className="d-flex flex-column gap-2">
                  <div className="claim-detail-info-card">
                    {firstHeaderColumn(expenseClaimHeaders)?.map(item => (
                      <div className="d-flex flex-row gap-3">
                        <div className="claim-detail-icon">
                          <img
                            src={item.icon}
                            alt="Claim Number Icon"
                            className="claim-icon-img"
                          />
                        </div>
                        <div className="claim-detail-info">
                          <div className="claim-title">{item?.title}</div>
                          <div className="claim-item">{item?.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col className="d-flex flex-column gap-2">
                  <div className="claim-detail-info-card">
                    {secondHeaderColumn(expenseClaimHeaders)?.map(item => (
                      <div className="d-flex flex-row gap-3">
                        <div className="claim-detail-icon">
                          <img
                            src={item.icon}
                            alt="Claim Number Icon"
                            className="claim-icon-img"
                          />
                        </div>
                        <div className="claim-detail-info">
                          <div className="claim-title">{item?.title}</div>
                          <div className="claim-item">{item?.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
                <Col className="d-flex flex-column gap-2">
                  <div className="claim-detail-info-card">
                    {thirdHeaderColumn(expenseClaimHeaders)?.map(item => (
                      <div className="d-flex flex-row gap-3">
                        <div className="claim-detail-icon">
                          <img
                            src={item.icon}
                            alt="Claim Number Icon"
                            className="claim-icon-img"
                          />
                        </div>
                        <div className="claim-detail-info">
                          <div className="claim-title">{item?.title}</div>
                          <div className="claim-item">{item?.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Col>
              </div>
            </div>
            {/* Body + Table Form */}
            <div className="table-responsive eecd-table-container">
              <Table className="">
                <thead>
                  <tr>
                    <th className="fw-semibold">Category</th>
                    <th className="fw-semibold">Type</th>
                    <th className="fw-semibold">Bill Date</th>
                    <th className="fw-semibold">Curr.</th>
                    <th className="fw-semibold">Ex.Rate</th>
                    <th className="fw-semibold">Amount</th>
                    <th className="fw-semibold">VAT</th>
                    <th className="fw-semibold">Remarks</th>
                    <th className="fw-semibold">Attachment</th>
                    <th className="fw-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="eec-table-body">{renderTableBody()}</tbody>
              </Table>
            </div>
            {/* Footer */}
            <div className="eec-footer p-3">
              <div className="eec-footer-btns">
                <div className="eec-footer-btns-left">
                  <Button
                    type="button"
                    className="custom-light-btn download-btn"
                    onClick={handlePrintExcel}
                  >
                    Download as Excel
                    <img
                      src={downloadExcelIcon}
                      className="eec-footer-icon"
                      alt="Download as Excel"
                    />
                  </Button>
                  <Button
                    type="button"
                    className="custom-light-btn print-btn"
                    onClick={handlePrint}
                  >
                    Print
                    <img
                      src={printIcon}
                      className="eec-footer-icon"
                      alt="Print"
                    />
                  </Button>
                </div>
                <div className="eec-footer-btns-right">
                  <div className="eec-summary-top me-1">
                    <div
                      className="eec-summary-title me-1"
                      title="Shows the total amount of the expense claim."
                    >
                      {`Expense Total (${expenseClaimHeaders?.currency || 'Currency'}) :`}
                    </div>
                    <div className="text-dark fw-bol expense-total-container">
                      {NumberUtil.truncateNumber(
                        expenseClaimHeaders?.claimAmt || 0,
                        2,
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                  {checkHRRoute() && (
                    <Button
                      type="button"
                      className="custom-secondary-btn"
                      onClick={handleTransaction}
                    >
                      Transaction
                    </Button>
                  )}
                  <div className="d-flex flex-row align-items-center gap-2">
                    <Button
                      type="button"
                      className="custom-secondary-btn"
                      onClick={handleAttachments}
                      disabled={!expenseClaimHeaders?.claimNo}
                    >
                      Attachment
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
      <ModalWrapper
        isModalOpen={isModalOpen}
        toggle={() => setIsModalOpen(!isModalOpen)}
        title={modalConfig.title}
        onButtonClick={() => setIsModalOpen(false)}
        buttonText="Close"
        size={modalConfig.size || 'lg'}
      >
        <ModalTable
          data={modalConfig.data}
          dataConfig={modalConfig.dataConfig}
          index
        />
      </ModalWrapper>
      <PrintModal
        isOpen={isPrintModalOpen}
        toggle={() => setIsPrintModalOpen(!isPrintModalOpen)}
        claimNo={expenseClaimHeaders?.claimNo}
      />
      <UploadAttachmentsModal
        isOpen={uploadAttachmentsModalOpen}
        toggle={() =>
          setUploadAttachmentsModalOpen(!uploadAttachmentsModalOpen)
        }
        claimNo={expenseClaimHeaders?.claimNo || ''}
        formType={CLAIM_FORM_TYPES.VIEW}
        claimStatus={expenseClaimHeaders?.claimStatus}
        finCoCd={expenseClaimHeaders?.finCoCd}
      />
      <ViewAttachmentsModal
        isOpen={viewAttachmentsModalOpen}
        toggle={() => setViewAttachmentsModalOpen(false)}
        claimNo={expenseClaimHeaders?.claimNo}
        srNo={viewAttachmentSrNo}
        claimStatus={expenseClaimHeaders?.claimStatus}
        finCoCd={expenseClaimHeaders?.finCoCd}
      />
      <AdvanceRecoveryModal
        isOpen={advanceRecoveryModalOpen}
        toggle={() => setAdvanceRecoveryModalOpen(false)}
        claimNo={expenseClaimHeaders?.claimNo || ''}
        formType={CLAIM_FORM_TYPES.VIEW}
      />
      <DisplayModal
        isModalOpen={displayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
    </FormProvider>
  );
}

export default EmployeeExpenseClaims;
